import { useCallback, useContext, useEffect, useState } from 'react';
import styles from './cashflowCategory.module.css';
import { Typography } from 'antd';
import {
  convertDateToMonthYearNumberFormat,
  formatToINR,
} from '../../../../../Utils/utils';
import InfoTile from '../../../Components/infoTile';
import { accountCashflowMonthly } from '../../../utils/savingsUtils';
import { AppContext } from '../../../../../App';
import { ROUTES } from '../../../../../Utils/routes';
import { ReactComponent as IncomingBOB } from '../../../../../Assets/BOB/v2/incoming.svg';
import { ReactComponent as ExpenseBOB } from '../../../../../Assets/BOB/v2/expense.svg';
import { ReactComponent as InvestmentBOB } from '../../../../../Assets/BOB/v2/investment.svg';
import { AccountCashflowCategoryType } from '../../../utils/constants';

type CashflowCategoryType = {
  header?: string;
  variant: 'primary' | 'secondary';
  showIcon?: boolean;
};

const getTitleForCashflow = (
  type: 'CREDIT' | 'INVESTMENT' | 'DEBIT',
  route: keyof typeof ROUTES
) => {
  switch (type) {
    case 'CREDIT':
      if (route === ROUTES.DASHBOARD) return 'Money In';
      return 'Incoming';
    case 'DEBIT':
      if (route === ROUTES.DASHBOARD) return 'Money Out';
      return 'Expenses';
    case 'INVESTMENT':
      return 'Investments';
    default:
      return '';
  }
};

const CashflowCategory = ({
  header,
  variant = 'primary',
  showIcon = true,
}: CashflowCategoryType) => {
  const { Text } = Typography;
  const [cashflow, setCashflow] = useState<
    Record<AccountCashflowCategoryType, number>
  >({
    credit: 0,
    investment: 0,
    debit: 0,
  });
  const {
    statusData,
    data,
    forceRender,
    isAccountDetailsFetched,
    currentRoute,
    isAmountVisible,
    accountsData: accountsListFromAnalyticsStatus,
  }: any = useContext(AppContext);

  const getInitialCashFlowCurrentMonth = useCallback(() => {
    const { credit, debit, investment } = accountCashflowMonthly(
      data,
      statusData,
      accountsListFromAnalyticsStatus,
      convertDateToMonthYearNumberFormat()
    );
    setCashflow({ credit, debit, investment });
  }, [accountsListFromAnalyticsStatus, data, statusData, forceRender]);

  useEffect(() => {
    if (
      isAccountDetailsFetched &&
      data.current?.ACCOUNT_DETAILS?.accountProfileList
    ) {
      getInitialCashFlowCurrentMonth();
    }
  }, [data, getInitialCashFlowCurrentMonth, isAccountDetailsFetched]);

  const cashflowCategoryMap = [
    {
      type: 'CREDIT',
      Icon: IncomingBOB,
      title: getTitleForCashflow('CREDIT', currentRoute),
      subtitle: formatToINR(cashflow?.credit?.toString(), isAmountVisible),
    },
    {
      type: 'DEBIT',
      Icon: ExpenseBOB,
      title: getTitleForCashflow('DEBIT', currentRoute),
      subtitle: formatToINR(cashflow?.debit?.toString(), isAmountVisible),
    },
    {
      type: 'INVESTMENT',
      Icon: InvestmentBOB,
      title: getTitleForCashflow('INVESTMENT', currentRoute),
      subtitle: formatToINR(cashflow?.investment?.toString(), isAmountVisible),
    },
  ];

  return (
    <div className={`${styles.container} ${styles[variant]}`}>
      {header && <Text className={styles.cashflowHeader}>{header}</Text>}
      <div className={`${styles.cashflowCategoryBody}`}>
        {cashflowCategoryMap.map((categories) => {
          const { Icon, title, subtitle, type } = categories;
          const CategoryIcon = showIcon ? (
            <Icon className={styles[type]} />
          ) : (
            <></>
          );
          return (
            <div
              className={`${styles.infoTyleWrapper} ${styles[variant + 'TileWrapper']}`}
              key={categories.title}
            >
              <InfoTile
                icon={CategoryIcon}
                subtitle={subtitle}
                title={title}
                color='black'
                variant={variant}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CashflowCategory;
